import type { Agent, CreditInfo, Device, Subscription } from '@/types/domain'
import { defineStore } from 'pinia'
import { agentApi, creditApi, deviceApi, subscriptionApi } from '@/api'
import { getStorage, removeStorage, setStorage } from '@/platform/storage'
import { useAuthStore } from './auth'

const VIRTUAL_MAC_KEY = 'virtualMacAddress'
const LAST_DEVICE_KEY = 'lastUsedDeviceId'
const MAC_PATTERN = /^(?:[0-9A-F]{2}:){5}[0-9A-F]{2}$/i

export function isMacAddress(value: string | null | undefined): value is string {
  return Boolean(value && MAC_PATTERN.test(value))
}

/**
 * 连接 WebSocket 必须用冒号 MAC 作为 device-id（服务端按 mac_address 查设备）。
 * 历史脏数据的 id 可能是 UUID，这里只返回 MAC 格式的候选值。
 */
export function deviceMacAddress(device: Device | null | undefined): string {
  if (!device)
    return ''
  for (const candidate of [device.macAddress, device.deviceId, device.id]) {
    if (isMacAddress(candidate))
      return candidate
  }
  return ''
}

function generateVirtualMac() {
  const bytes = crypto.getRandomValues(new Uint8Array(6))
  bytes[0] = ((bytes[0] || 0) & 0xFE) | 0x02
  return [...bytes].map(value => value.toString(16).padStart(2, '0').toUpperCase()).join(':')
}

function deviceIdentifier(device: Device) {
  return device.macAddress || device.deviceId || device.id || ''
}

export const useAppStore = defineStore('app', {
  state: () => ({
    bootstrapped: false,
    agents: [] as Agent[],
    devices: [] as Device[],
    credit: null as CreditInfo | null,
    subscription: null as Subscription | null,
    activeAgentId: '',
    activeDeviceId: getStorage(LAST_DEVICE_KEY),
  }),
  getters: {
    activeAgent: state => state.agents.find(agent => agent.id === state.activeAgentId) || state.agents[0],
    activeDevice: state => state.devices.find(device => deviceIdentifier(device) === state.activeDeviceId) || state.devices[0],
  },
  actions: {
    async bootstrap() {
      const auth = useAuthStore()
      await auth.loadPublicConfig()
      if (auth.isLoggedIn) {
        await Promise.allSettled([auth.loadUser(), this.refreshDashboard()])
      }
      this.bootstrapped = true
    },
    async refreshDashboard() {
      const [agents, devices, credit, subscription] = await Promise.allSettled([
        agentApi.list(),
        deviceApi.list(),
        creditApi.info(),
        subscriptionApi.info(),
      ])

      if (agents.status === 'fulfilled') {
        this.agents = agents.value || []
        if (!this.activeAgentId && this.agents[0]) {
          this.activeAgentId = this.agents[0].id
        }
      }
      if (devices.status === 'fulfilled') {
        this.devices = Array.isArray(devices.value) ? devices.value : devices.value.list || []
        this.syncActiveDevice()
      }
      if (credit.status === 'fulfilled') {
        this.credit = credit.value
      }
      if (subscription.status === 'fulfilled') {
        this.subscription = subscription.value
      }
    },
    syncActiveDevice() {
      if (!this.devices.length) {
        this.activeDeviceId = ''
        removeStorage(LAST_DEVICE_KEY)
        return
      }
      const selected = this.devices.find(device => deviceIdentifier(device) === this.activeDeviceId) || this.devices[0]
      this.activeDeviceId = selected ? deviceIdentifier(selected) : ''
      if (this.activeDeviceId)
        setStorage(LAST_DEVICE_KEY, this.activeDeviceId)
    },
    async ensureChatDevice(agentId: string) {
      if (!this.devices.length) {
        const response = await deviceApi.list({ page: 1, limit: 100 })
        this.devices = Array.isArray(response) ? response : response.list || []
        this.syncActiveDevice()
      }
      // 只把有合法 MAC 的设备当可用设备：脏数据（mac 为空、id 为 UUID）无法建立会话，
      // 直接走虚拟设备重建流程
      if (deviceMacAddress(this.activeDevice))
        return this.activeDevice
      const usable = this.devices.find(device => deviceMacAddress(device))
      if (usable) {
        this.activeDeviceId = deviceIdentifier(usable)
        setStorage(LAST_DEVICE_KEY, this.activeDeviceId)
        return usable
      }

      const auth = useAuthStore()
      if (!auth.token)
        throw new Error('未登录，无法初始化聊天设备')

      let virtualMac = getStorage(VIRTUAL_MAC_KEY)
      if (!isMacAddress(virtualMac)) {
        virtualMac = generateVirtualMac()
        setStorage(VIRTUAL_MAC_KEY, virtualMac)
      }

      try {
        const code = await deviceApi.registerVirtual(virtualMac)
        await deviceApi.bind(agentId, code)
      }
      catch (error) {
        removeStorage(VIRTUAL_MAC_KEY)
        throw error
      }

      const response = await deviceApi.list({ page: 1, limit: 100 })
      this.devices = Array.isArray(response) ? response : response.list || []
      const created = this.devices.find(device => device.macAddress?.toUpperCase() === virtualMac.toUpperCase())
      if (!created)
        throw new Error('虚拟设备初始化后未出现在设备列表中')
      this.activeDeviceId = deviceIdentifier(created)
      setStorage(LAST_DEVICE_KEY, this.activeDeviceId)
      return created
    },
  },
})
